// ─── GenrePickerModal ──────────────────────────────────────────────────────
// Story-mode genre picker. Opens when the student clicks "Produce Story film".
// Visual treatment mirrors Super Narrate's "Choose a flavour" grid — clean
// light cards on the standard Deskey surface, each card carrying a real
// Nano-Banana-generated poster bespoke to the LESSON TOPIC re-imagined in
// the genre. Lazy-fetches the poster set on open; renders a shimmer
// placeholder until each tile resolves.
//
// Persists the last-chosen genre in localStorage so returning students
// don't have to re-pick every time.
import React, { useEffect, useState } from 'react';
import { X, Check, ArrowRight } from 'lucide-react';
import api from '../../studentApi';

const LS_KEY = 'desky_video_studio_last_genre';
const TINT   = '#F08A24';

export default function GenrePickerModal({ open, genres, onClose, onConfirm, lessonId }) {
  const fallbackId = (genres && genres[0]?.id) || 'cinematic-drama';
  const [selected, setSelected] = useState(() =>
    localStorage.getItem(LS_KEY) || fallbackId
  );
  const [posters, setPosters] = useState({});       // { genre_id: data_url }
  const [postersLoading, setPostersLoading] = useState(false);

  useEffect(() => {
    if (open && genres?.length) {
      if (!genres.some(g => g.id === selected)) setSelected(genres[0].id);
    }
  }, [open, genres, selected]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Fetch lesson-aware poster artwork on open. Backend caches per
  // (lesson_id, kind, genre_id), so the first opening of a lesson takes
  // ~15-20 s and every subsequent open is instant.
  useEffect(() => {
    if (!open || !lessonId || !genres?.length) return;
    let cancelled = false;
    (async () => {
      setPostersLoading(true);
      try {
        const r = await api.post(`/student/lesson/${lessonId}/genre-posters`, {
          kind:   'video',
          genres: genres.map(g => ({
            id:      g.id,
            label:   g.label,
            blurb:   g.tagline || g.blurb || '',
            tagline: g.tagline || '',
          })),
        });
        if (cancelled) return;
        const map = r?.posters || {};
        // Backend returns relative paths like /student/genre-posters/...
        // Prefix with the API base so the <img> tags can resolve them.
        const base = `${process.env.REACT_APP_BACKEND_URL}/api`;
        const absMap = {};
        Object.entries(map).forEach(([k, v]) => {
          absMap[k] = v && v.startsWith('/') ? `${base}${v}` : v;
        });
        setPosters(absMap);
      } catch (e) {
        // Silent — cards fall back to gradient placeholders.
      } finally {
        if (!cancelled) setPostersLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, lessonId, genres]);

  const confirm = () => {
    if (!selected) return;
    localStorage.setItem(LS_KEY, selected);
    onConfirm?.(selected);
  };

  if (!open) return null;

  return (
    <div
      data-testid="genre-picker-modal"
      className="fixed inset-0 z-[80] flex items-center justify-center px-4"
      role="dialog" aria-modal="true" aria-labelledby="genre-picker-title">
      <div className="absolute inset-0"
        style={{ background: 'rgba(8,8,14,0.55)', backdropFilter: 'blur(4px)' }}
        onClick={onClose} />

      <div className="relative w-full max-w-4xl overflow-hidden shadow-2xl"
        style={{
          background:    'var(--st-bg)',
          border:        '1px solid var(--st-border)',
          borderRadius:  16,
          maxHeight:     '92vh',
        }}>
        {/* Header — matches Super Narrate breadcrumb + serif title hierarchy */}
        <div className="flex items-start justify-between px-7 pt-6 pb-5"
          style={{ borderBottom: '1px solid var(--st-border)' }}>
          <div>
            <div className="text-[10px] font-extrabold tracking-[0.32em] uppercase"
              style={{ color: 'var(--st-mute)' }}>
              <span style={{ color: 'var(--st-mute)' }}>Studio</span>
              <span className="mx-1.5 opacity-40">/</span>
              <span style={{ color: 'var(--st-mute)' }}>Story Commission</span>
              <span className="mx-1.5 opacity-40">/</span>
              <span style={{ color: TINT }}>Visual Language</span>
            </div>
            <h2 id="genre-picker-title"
              className="text-[22px] sm:text-[26px] font-black tracking-tight leading-[1.1] mt-1.5"
              style={{ color: 'var(--st-ink)', fontFamily: 'var(--st-font-heading)' }}>
              Choose the visual language for your film.
            </h2>
            <p className="text-[12.5px] mt-1.5 max-w-xl leading-relaxed"
              style={{ color: 'var(--st-mute)' }}>
              The genre locks in palette, lens, lighting and era — and the
              cover artwork below is rendered live from your lesson topic.
            </p>
          </div>
          <button onClick={onClose}
            data-testid="genre-picker-close"
            className="w-9 h-9 inline-flex items-center justify-center rounded-full transition-colors hover:bg-[var(--st-card-2)]"
            style={{ border: '1px solid var(--st-border)' }}
            aria-label="Close">
            <X className="w-4 h-4" style={{ color: 'var(--st-mute)' }} />
          </button>
        </div>

        {/* Poster grid */}
        <div className="px-6 py-5 overflow-y-auto"
          style={{ maxHeight: 'calc(92vh - 220px)' }}>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {(genres || []).map(g => (
              <GenrePosterCard
                key={g.id}
                genre={g}
                selected={selected === g.id}
                posterUrl={posters[g.id]}
                loading={postersLoading && !posters[g.id]}
                onClick={() => setSelected(g.id)}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-7 py-4"
          style={{ borderTop: '1px solid var(--st-border)', background: 'var(--st-card)' }}>
          <div className="text-[10.5px] font-extrabold uppercase tracking-[0.22em] inline-flex items-center gap-1.5"
            style={{ color: 'var(--st-mute)' }}>
            <span className="inline-block w-1.5 h-1.5 rounded-full"
              style={{ background: postersLoading ? TINT : 'var(--st-mute)' }} />
            {postersLoading ? 'Rendering lesson-aware cover art…' : 'Cover art rendered from this lesson'}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose}
              data-testid="genre-picker-cancel"
              className="text-[11px] font-extrabold uppercase tracking-[0.22em] px-4 py-2 rounded-full transition-colors hover:bg-[var(--st-card-2)]"
              style={{ border: '1px solid var(--st-border)', color: 'var(--st-ink)' }}>
              Cancel
            </button>
            <button onClick={confirm}
              data-testid="genre-picker-confirm"
              disabled={!selected}
              className="text-[11px] font-extrabold uppercase tracking-[0.22em] px-5 py-2.5 rounded-full inline-flex items-center gap-1.5 disabled:opacity-50 transition-transform hover:translate-x-0.5"
              style={{
                background: TINT,
                color:      '#fff',
                boxShadow:  `0 10px 24px -10px ${TINT}88`,
              }}>
              Greenlight production <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Poster card (Netflix-style) ──────────────────────────────────────────
function GenrePosterCard({ genre, selected, posterUrl, loading, onClick }) {
  const accent  = genre.accent_hex || genre.accent || TINT;
  return (
    <button
      onClick={onClick}
      data-testid={`genre-card-${genre.id}`}
      aria-pressed={selected}
      className="group relative text-left overflow-hidden transition-all duration-300 hover:-translate-y-0.5 focus:outline-none"
      style={{
        aspectRatio:  '2 / 3',
        borderRadius: 14,
        background:   'var(--st-card)',
        border:      `1.5px solid ${selected ? TINT : 'var(--st-border)'}`,
        boxShadow:    selected
          ? `0 16px 32px -16px ${TINT}66, 0 0 0 3px ${TINT}22`
          : '0 4px 14px -10px rgba(15,23,42,0.18)',
      }}>
      {/* Poster artwork — real Nano Banana when available, soft accent
          wash + shimmer while loading, accent-tinted placeholder if the
          generation failed. */}
      {posterUrl ? (
        <img src={posterUrl} alt=""
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          loading="lazy"
          draggable={false} />
      ) : (
        <div className="absolute inset-0"
          style={{
            background: `linear-gradient(155deg, ${accent} 0%, color-mix(in srgb, ${accent} 40%, var(--st-card)) 100%)`,
          }}>
          {loading && (
            <div className="absolute inset-0 animate-pulse"
              style={{
                background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.25) 50%, transparent 100%)',
              }} />
          )}
        </div>
      )}

      {/* Bottom vignette so the title reads cleanly over any poster. */}
      <span aria-hidden="true" className="absolute inset-x-0 bottom-0 h-1/2 pointer-events-none"
        style={{ background: 'linear-gradient(180deg, transparent 0%, rgba(8,8,14,0.85) 75%, rgba(8,8,14,0.96) 100%)' }} />

      {/* Top meta — selected check only (genre label is already rendered
          big at the bottom; the redundant "Genre" pill was visual noise). */}
      <div className="relative z-10 flex items-start justify-end gap-2 p-2.5 min-h-[28px]">
        {selected && (
          <span className="w-5 h-5 rounded-full inline-flex items-center justify-center"
            style={{ background: TINT, color: '#fff' }}>
            <Check className="w-3 h-3" />
          </span>
        )}
      </div>

      {/* Title overlay */}
      <div className="absolute inset-x-0 bottom-0 z-10 p-3">
        <div className="text-[14.5px] sm:text-[15.5px] font-black tracking-tight leading-[1.05] drop-shadow"
          style={{ fontFamily: 'var(--st-font-heading)', color: '#fff' }}>
          {genre.label}
        </div>
        <div className="text-[10.5px] mt-0.5 leading-snug line-clamp-2"
          style={{ color: 'rgba(255,255,255,0.78)' }}>
          {genre.tagline || genre.blurb}
        </div>
      </div>
    </button>
  );
}
